// src/components/DraggableColorPicker.js
import { useState, useEffect, useRef } from "react";
import { Gradient } from "fabric";
import { useData } from "../hooks/useData";
import useCanvasHistory from "../hooks/useCanvasHistory";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import GradientColorPickerComp from "./GradientColorPickerComp";

const DraggableColorPicker = ({ canvas, onClose }) => {
  const { selectedObject, setSelectedObject, setSnackbar } = useData();
  const { saveHistory } = useCanvasHistory(canvas);
  const [gradientValue, setGradientValue] = useState("rgba(0,128,128,1)");
  const [position, setPosition] = useState({
    x: window.innerWidth / 2,
    y: 80,
  });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const dialogRef = useRef(null);

  // Initialize gradient value based on selected object
  useEffect(() => {
    if (selectedObject) {
      // Solid color or tint filter
      if (selectedObject.fill && typeof selectedObject.fill === "string") {
        setGradientValue(selectedObject.fill);
      } else if (
        selectedObject.filters &&
        selectedObject.filters.some((filter) => filter.type === "Tint")
      ) {
        const tintFilter = selectedObject.filters.find(
          (filter) => filter.type === "Tint"
        );
        const color = tintFilter.color || "#000000";
        const opacity = selectedObject.opacity || 1;
        setGradientValue(
          `${color}${Math.round(opacity * 255)
            .toString(16)
            .padStart(2, "0")}`
        );
      } else if (
        selectedObject.fill &&
        selectedObject.fill instanceof Gradient
      ) {
        // Gradient
        const { type, angle, colorStops } = selectedObject.fill;
        const colors = colorStops
          .map((stop) => {
            const color = stop.color;
            const opacity = stop.opacity !== undefined ? stop.opacity : 1;
            return opacity < 1
              ? `${color}${Math.round(opacity * 255)
                  .toString(16)
                  .padStart(2, "0")}`
              : color;
          })
          .join(", ");
        const gradientString = `${type}-gradient(${angle || 0}deg, ${colors})`;
        setGradientValue(gradientString);
      }
    }
  }, [selectedObject]);

  const parseGradientString = (gradientString) => {
    const regex = /(linear|radial)-gradient\((\d+)deg,\s*(.+)\)/;
    const match = gradientString.match(regex);
    if (!match) return null;

    const [, type, angle, colors] = match;
    const colorStops = colors.split(",").map((color, index) => {
      color = color.trim();
      let opacity = 1;
      // Extract opacity from hex with alpha (e.g., #FF0000FF)
      if (color.match(/^#[0-9A-Fa-f]{6}[0-9A-Fa-f]{2}$/)) {
        opacity = parseInt(color.slice(-2), 16) / 255;
        color = color.slice(0, -2);
      } else if (color.match(/^#[0-9A-Fa-f]{8}$/)) {
        // Handle #RRGGBBAA
        opacity = parseInt(color.slice(-2), 16) / 255;
        color = color.slice(0, -2);
      }
      return {
        offset: index / (colors.split(",").length - 1),
        color,
        opacity,
      };
    });

    return {
      type,
      angle: parseInt(angle),
      colorStops,
    };
  };

  const applyColorAndOpacity = (value) => {
    if (!selectedObject) {
      setSnackbar({
        open: true,
        message: "No object selected to apply color or opacity.",
        severity: "error",
      });
      return;
    }

    try {
      if (selectedObject.fill !== undefined) {
        // For shapes (e.g., rect, circle, polygon)
        if (value.includes("gradient")) {
          // Gradient
          const gradientData = parseGradientString(value);
          if (!gradientData) {
            setSnackbar({
              open: true,
              message: "Invalid gradient format.",
              severity: "error",
            });
            return;
          }

          const { type, angle, colorStops } = gradientData;
          selectedObject.set({
            fill: new Gradient({
              type: type.toLowerCase(),
              gradientUnits: "pixels",
              coords:
                type === "linear"
                  ? {
                      x1: 0,
                      y1: 0,
                      x2: 100 * Math.cos((angle * Math.PI) / 180),
                      y2: 100 * Math.sin((angle * Math.PI) / 180),
                    }
                  : { x1: 50, y1: 50, x2: 50, y2: 50, r1: 0, r2: 50 },
              colorStops,
            }),
            opacity: 1, // Opacity handled in gradient color stops
          });
        } else {
          // Solid color
          let color = value;
          let opacity = 1;
          // Extract opacity from RGBA or hex with alpha
          if (color.match(/^#[0-9A-Fa-f]{6}[0-9A-Fa-f]{2}$/)) {
            opacity = parseInt(color.slice(-2), 16) / 255;
            color = color.slice(0, -2);
          } else if (color.match(/^#[0-9A-Fa-f]{8}$/)) {
            opacity = parseInt(color.slice(-2), 16) / 255;
            color = color.slice(0, -2);
          } else if (color.startsWith("rgba")) {
            const rgbaMatch = color.match(
              /rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/
            );
            if (rgbaMatch) {
              const [, r, g, b, a] = rgbaMatch;
              color = `#${parseInt(r).toString(16).padStart(2, "0")}${parseInt(
                g
              )
                .toString(16)
                .padStart(2, "0")}${parseInt(b).toString(16).padStart(2, "0")}`;
              opacity = parseFloat(a);
            }
          }
          selectedObject.set({
            fill: color,
            opacity,
          });
        }
      } else if (selectedObject.type === "image") {
        // For images (no gradient support)
        if (value.includes("gradient")) {
          setSnackbar({
            open: true,
            message: "Images do not support gradient fills.",
            severity: "warning",
          });
          return;
        }
        let color = value;
        let opacity = 1;
        if (color.match(/^#[0-9A-Fa-f]{6}[0-9A-Fa-f]{2}$/)) {
          opacity = parseInt(color.slice(-2), 16) / 255;
          color = color.slice(0, -2);
        } else if (color.match(/^#[0-9A-Fa-f]{8}$/)) {
          opacity = parseInt(color.slice(-2), 16) / 255;
          color = color.slice(0, -2);
        } else if (color.startsWith("rgba")) {
          const rgbaMatch = color.match(
            /rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/
          );
          if (rgbaMatch) {
            const [, r, g, b, a] = rgbaMatch;
            color = `#${parseInt(r).toString(16).padStart(2, "0")}${parseInt(g)
              .toString(16)
              .padStart(2, "0")}${parseInt(b).toString(16).padStart(2, "0")}`;
            opacity = parseFloat(a);
          }
        }
        const tintFilter = new Image.filters.Tint({
          color,
          opacity,
        });
        selectedObject.filters = selectedObject.filters.filter(
          (filter) => filter.type !== "Tint"
        );
        selectedObject.filters.push(tintFilter);
        selectedObject.applyFilters();
        selectedObject.set({ opacity });
      } else {
        setSnackbar({
          open: true,
          message: "Selected object does not support color or opacity.",
          severity: "warning",
        });
        return;
      }

      setSelectedObject(selectedObject);
      canvas.requestRenderAll();
      saveHistory(canvas);
    } catch (error) {
      console.error("Error applying color or gradient:", error);
      setSnackbar({
        open: true,
        message: "Error applying changes. Please try again.",
        severity: "error",
      });
    }
  };

  const handleChangeColor = (newValue) => {
    setGradientValue(newValue);
    applyColorAndOpacity(newValue);
  };

  const handleMouseDown = (e) => {
    setDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = (e) => {
    if (dragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setDragging(false);
  };

  useEffect(() => {
    if (dragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragging, dragStart, handleMouseMove]);

  return (
    <Dialog
      open={true}
      onClose={onClose}
      PaperProps={{
        ref: dialogRef,
        sx: {
          position: "absolute",
          top: position.y,
          left: position.x,
          m: 0,
          bgcolor: "background.paper",
          boxShadow: 3,
          borderRadius: 1,
          width: 280,
          zIndex: 1400,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "move",
          p: 1,
        }}
        onMouseDown={handleMouseDown}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DragIndicatorIcon sx={{ cursor: "move" }} />
          <span>Color Picker</span>
        </Box>
        <Tooltip title="Close">
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close color picker"
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        <GradientColorPickerComp handleChangeColor={handleChangeColor} />
      </DialogContent>
    </Dialog>
  );
};

export default DraggableColorPicker;
